import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Edit2, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

export default function ProfilePage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    username: "",
    email: "",
    phone: "",
    dob: "",
  });
  const [addressForm, setAddressForm] = useState({
    label: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "India",
    id: null,
  });

  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");
  const API_URL = `https://accessoriesgirly.onrender.com/api/users/${userId}`;

  const fetchUser = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await axios.get(API_URL);
      setUser(res.data.user);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
      alert("Failed to fetch user data");
    }
  }, [API_URL, userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  if (loading) return <p className="text-center mt-20">Loading...</p>;

  if (!userId)
    return (
      <div className="text-center py-20">
        <p className="mb-4 text-lg font-medium text-gray-700">
          Please Login First
        </p>
        <button
          onClick={() => navigate("/login")}
          className="px-6 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition"
        >
          Go to Login
        </button>
      </div>
    );

  if (!user) return <p className="text-center mt-20">No user data found.</p>;

  // Open profile modal
  const openProfileModal = () => {
    setProfileForm({
      username: user.username,
      email: user.email,
      phone: user.phone || "",
      dob: user.dob ? new Date(user.dob).toISOString().split("T")[0] : "",
    });
    setIsProfileModalOpen(true);
  };

  // Open address modal
  const openAddressModal = (addr = null) => {
    if (addr) {
      setAddressForm({ ...addr, id: addr._id });
    } else {
      setAddressForm({
        label: "",
        street: "",
        city: "",
        state: "",
        zip: "",
        country: "India",
        id: null,
      });
    }
    setIsAddressModalOpen(true);
  };

  // Update profile
  const handleProfileSubmit = async () => {
    try {
      const res = await axios.put(API_URL, profileForm);
      setUser(res.data.user);
      setIsProfileModalOpen(false);
      alert("Profile updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to update profile");
    }
  };

  // Add/Edit address
  const handleAddressSubmit = async () => {
    try {
      let updatedAddresses;
      if (addressForm.id) {
        updatedAddresses = user.addresses.map((addr) =>
          addr._id === addressForm.id ? { ...addressForm } : addr
        );
      } else {
        updatedAddresses = [...user.addresses, { ...addressForm }];
      }

      const res = await axios.put(API_URL, { addresses: updatedAddresses });
      setUser(res.data.user);
      setIsAddressModalOpen(false);
      setAddressForm({
        label: "",
        street: "",
        city: "",
        state: "",
        zip: "",
        country: "India",
        id: null,
      });
      alert(
        addressForm.id
          ? "Address updated successfully!"
          : "Address added successfully!"
      );
    } catch (err) {
      console.error(err);
      alert("Failed to save address");
    }
  };

  return (
    <section className="py-12 bg-pink-50 min-h-screen">
      <div className="max-w-4xl mx-auto p-4">
        <h1 className="text-3xl font-bold text-pink-600 mb-8 text-center">
          My Profile
        </h1>

        {/* Personal Info */}
        <div className="bg-white shadow-md rounded-xl p-6 mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-semibold text-pink-600 mb-2">
              Personal Info
            </h2>
            <p className="text-gray-700">Username: {user.username}</p>
            <p className="text-gray-700">Email: {user.email}</p>
            <p className="text-gray-700">Phone: {user.phone || "-"}</p>
            <p className="text-gray-700">
              DOB: {user.dob ? new Date(user.dob).toLocaleDateString() : "-"}
            </p>
          </div>
          <button
            onClick={openProfileModal}
            className="flex items-center gap-2 bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700"
          >
            <Edit2 size={18} /> Edit
          </button>
        </div>

        {/* Addresses */}
        <div className="bg-white shadow-md rounded-xl p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-pink-600">Addresses</h2>
            <button
              onClick={() => openAddressModal()}
              className="flex items-center gap-1 bg-pink-600 text-white px-3 py-1 rounded-lg hover:bg-pink-700"
            >
              <Plus size={16} /> Add
            </button>
          </div>
          {user.addresses.map((addr) => (
            <div
              key={addr._id}
              className="flex justify-between items-center border-b py-2 last:border-b-0"
            >
              <div>
                <p className="font-semibold">{addr.label}</p>
                <p className="text-gray-600">{`${addr.street}, ${addr.city}, ${
                  addr.state || ""
                }, ${addr.zip || ""}, ${addr.country}`}</p>
              </div>
              <button
                onClick={() => openAddressModal(addr)}
                className="text-pink-600 hover:text-pink-800"
              >
                <Edit2 size={18} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Profile Modal */}
      {isProfileModalOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            className="bg-white rounded-xl p-6 w-full max-w-md relative"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
          >
            <h2 className="text-xl font-bold text-pink-600 mb-4">
              Edit Profile
            </h2>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Username"
                value={profileForm.username}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, username: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
              <input
                type="email"
                placeholder="Email"
                value={profileForm.email}
                disabled
                onChange={(e) =>
                  setProfileForm({ ...profileForm, email: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Phone"
                value={profileForm.phone}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, phone: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
              <input
                type="date"
                value={profileForm.dob}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, dob: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setIsProfileModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleProfileSubmit}
                className="px-4 py-2 rounded-lg bg-pink-600 text-white hover:bg-pink-700"
              >
                Save
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Address Modal */}
      {isAddressModalOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div
            className="bg-white rounded-xl p-6 w-full max-w-md relative"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
          >
            <h2 className="text-xl font-bold text-pink-600 mb-4">
              {addressForm.id ? "Edit Address" : "Add Address"}
            </h2>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Label (Home/Work)"
                value={addressForm.label}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, label: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Street"
                value={addressForm.street || ""}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, street: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="City"
                value={addressForm.city || ""}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, city: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="State"
                value={addressForm.state || ""}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, state: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="ZIP Code"
                value={addressForm.zip || ""}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, zip: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Country"
                value={addressForm.country || "India"}
                onChange={(e) =>
                  setAddressForm({ ...addressForm, country: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:outline-none"
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setIsAddressModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleAddressSubmit}
                className="px-4 py-2 rounded-lg bg-pink-600 text-white hover:bg-pink-700"
              >
                Save
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
}
